import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

type ChatWorkspaceContextType = {
  threadListVersion: number;
  bumpThreadListRefresh: () => void;
  /**
   * Threads known client-side to be running deep research. Lets the history
   * sidebar show the "research pending" dot the moment generation starts —
   * before the server-side research_pending flag round-trips on a refetch — and
   * keep it shown as the user navigates between chats and other pages (this
   * provider outlives the chat window, so the flag survives its unmount).
   */
  pendingResearchThreadIds: Set<string>;
  markResearchPending: (threadId: string) => void;
  clearResearchPending: (threadId: string) => void;
  /** Same idea as pendingResearchThreadIds, for a running bulk image batch. */
  pendingImageBatchThreadIds: Set<string>;
  markImageBatchPending: (threadId: string) => void;
  clearImageBatchPending: (threadId: string) => void;
};

const ChatWorkspaceContext = createContext<ChatWorkspaceContextType | undefined>(
  undefined
);

export const ChatWorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [threadListVersion, setThreadListVersion] = useState(0);
  const [pendingResearchThreadIds, setPendingResearchThreadIds] = useState<
    Set<string>
  >(() => new Set());
  const [pendingImageBatchThreadIds, setPendingImageBatchThreadIds] = useState<
    Set<string>
  >(() => new Set());

  const bumpThreadListRefresh = useCallback(() => {
    setThreadListVersion((v) => v + 1);
  }, []);

  const markResearchPending = useCallback((threadId: string) => {
    setPendingResearchThreadIds((prev) => {
      if (prev.has(threadId)) return prev;
      const next = new Set(prev);
      next.add(threadId);
      return next;
    });
  }, []);

  const clearResearchPending = useCallback((threadId: string) => {
    setPendingResearchThreadIds((prev) => {
      if (!prev.has(threadId)) return prev;
      const next = new Set(prev);
      next.delete(threadId);
      return next;
    });
  }, []);

  const markImageBatchPending = useCallback((threadId: string) => {
    setPendingImageBatchThreadIds((prev) => {
      if (prev.has(threadId)) return prev;
      const next = new Set(prev);
      next.add(threadId);
      return next;
    });
  }, []);

  const clearImageBatchPending = useCallback((threadId: string) => {
    setPendingImageBatchThreadIds((prev) => {
      if (!prev.has(threadId)) return prev;
      const next = new Set(prev);
      next.delete(threadId);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      threadListVersion,
      bumpThreadListRefresh,
      pendingResearchThreadIds,
      markResearchPending,
      clearResearchPending,
      pendingImageBatchThreadIds,
      markImageBatchPending,
      clearImageBatchPending,
    }),
    [
      threadListVersion,
      bumpThreadListRefresh,
      pendingResearchThreadIds,
      markResearchPending,
      clearResearchPending,
      pendingImageBatchThreadIds,
      markImageBatchPending,
      clearImageBatchPending,
    ]
  );

  return (
    <ChatWorkspaceContext.Provider value={value}>
      {children}
    </ChatWorkspaceContext.Provider>
  );
};

export const useChatWorkspace = (): ChatWorkspaceContextType => {
  const ctx = useContext(ChatWorkspaceContext);
  if (!ctx) {
    throw new Error(
      "useChatWorkspace must be used within ChatWorkspaceProvider"
    );
  }
  return ctx;
};
